import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import { Node } from "prosemirror-model";
import type { EditorState, Transaction } from "prosemirror-state";

const isRow = (node: Node | undefined): node is Node => node?.type.spec.tableRole === "row";

// For every new cell, the index of the same old cell; null unless the new row is the old one minus some cells.
const keptCells = (oldRow: Node, newRow: Node): number[] | null => {
	const kept: number[] = [];
	let oldIndex = 0;
	for (let newIndex = 0; newIndex < newRow.childCount; newIndex++) {
		while (oldIndex < oldRow.childCount && !oldRow.child(oldIndex).eq(newRow.child(newIndex))) oldIndex++;
		if (oldIndex === oldRow.childCount) return null;
		kept.push(oldIndex++);
	}
	return kept;
};

export const isCellsOnlyDeletion = (change: DiffChange): boolean => {
	const oldRow = change.nodeA as Node | undefined;
	const newRow = change.nodeB as Node | undefined;
	if (change.type !== "modified" || !isRow(oldRow) || !isRow(newRow)) return false;
	return oldRow.childCount > newRow.childCount && !!keptCells(oldRow, newRow);
};

export const restoreDeletedColumn = (
	state: EditorState,
	changes: DiffChange[],
	table: { from: number; to: number },
	column: number,
): Transaction | null => {
	const tr = state.tr;
	changes.forEach((change) => {
		const oldRow = change.nodeA as Node | undefined;
		const newRow = change.nodeB as Node | undefined;
		if (change.posB === undefined || change.posB < table.from || change.posB > table.to) return;
		if (!isCellsOnlyDeletion(change) || !oldRow || !newRow || column >= oldRow.childCount) return;
		const kept = keptCells(oldRow, newRow);
		if (!kept || kept.includes(column)) return;

		let at = change.posB + 1;
		kept.forEach((oldIndex, newIndex) => {
			if (oldIndex < column) at += newRow.child(newIndex).nodeSize;
		});
		const cell = Node.fromJSON(state.schema, oldRow.child(column).toJSON());
		tr.insert(tr.mapping.map(at), cell);
	});
	return tr.docChanged ? tr : null;
};

type Range = { from: number; to: number };

const inTable = (change: DiffChange, table: Range) =>
	change.posB !== undefined && change.posB >= table.from && change.posB <= table.to;

export const deletedColumnsIn = (changes: DiffChange[], table: Range): Set<number> => {
	const columns = new Set<number>();
	changes.forEach((change) => {
		if (!inTable(change, table) || !isCellsOnlyDeletion(change)) return;
		const oldRow = change.nodeA as Node;
		const kept = keptCells(oldRow, change.nodeB as Node) ?? [];
		for (let index = 0; index < oldRow.childCount; index++) if (!kept.includes(index)) columns.add(index);
	});
	return columns;
};

const tableAround = (state: EditorState, pos: number): Range | undefined => {
	const Pos = state.doc.resolve(pos);
	for (let depth = Pos.depth; depth >= 0; depth--) {
		if (Pos.node(depth).type.spec.tableRole === "table")
			return depth === 0 ? undefined : { from: Pos.before(depth), to: Pos.after(depth) };
	}
	return undefined;
};

// An old row still has the cells of a column deleted since; put back as is it widens the table and
// prosemirror-tables pads every other row with empty cells.
export const fitRowsToTable = (state: EditorState, changes: DiffChange[], at: number, nodes: Node[]): Node[] => {
	if (!nodes.every(isRow) || at < 0 || at > state.doc.content.size) return nodes;
	const table = tableAround(state, at);
	if (!table) return nodes;
	const columns = deletedColumnsIn(changes, table);
	if (!columns.size) return nodes;
	return nodes.map((row) => {
		const cells: Node[] = [];
		row.forEach((cell, _, index) => {
			if (!columns.has(index)) cells.push(cell);
		});
		return row.type.create(row.attrs, cells, row.marks);
	});
};
